import { Link, router, Slot, usePathname, type Href } from "expo-router";
import { useEffect, useMemo, useRef } from "react";
import { View } from "react-native";
import Animated, { useSharedValue } from "react-native-reanimated";
import { Display, Hide, mq } from "react-native-unistyles";
import { getWebProps } from "react-native-unistyles/web";

import Icon from "$src/core/components/atoms/Icon";
import Image from "$src/core/components/atoms/Image/Image";
import Pressable from "$src/core/components/atoms/Pressable";
import Footer from "$src/core/components/organisms/Footer";
import NavBar from "$src/core/components/organisms/NavBar";
import TabBar from "$src/core/components/organisms/TabBar";
import useDebouncedCallback from "$src/core/hooks/use-debounced-callback";
import useLogo from "$src/core/hooks/use-logo";
import useMenu from "$src/core/hooks/use-menu";
import useTranslation from "$src/core/hooks/use-translation";
import isPathnameActive from "$src/core/utils/routing/is-pathname-active";

import $, { useAnimatedStyles } from "./TabsLayout.web.styles";
import type { TabsLayoutProps } from "./types";

const MAX_NAV_BAR_ITEMS = 7;
const MAX_TAB_BAR_ITEMS = 5;

const TabsLayout = ({ routes }: TabsLayoutProps) => {
  const pathname = usePathname();
  const menu = useMenu();
  const logo = useLogo();
  const { t } = useTranslation();

  const profileTab = menu?.tabBar.find((item) => item.type === "profile");
  const firstTab = menu?.tabBar[0];
  const profilePath = profileTab ? `/${profileTab.slug}` : "/storefront";
  const profileHref = profilePath as Href;
  const homeHref = (firstTab ? `/${firstTab.slug}` : "/storefront") as Href;

  const scrollY = useSharedValue(0);
  // Captured from the scroll event (not a render-time ref) so we can reset the
  // scroll position on navigation.
  const scrollElementRef = useRef<HTMLDivElement | null>(null);

  const { headerStyle } = useAnimatedStyles({ scrollY });

  const handleScroll = useDebouncedCallback(
    (event: React.UIEvent<HTMLDivElement>) => {
      if (event.target instanceof HTMLDivElement) {
        scrollElementRef.current = event.target;
        scrollY.value = event.target.scrollTop;
      }
    },
    16,
  );

  // The scroll container persists across navigations (only its `<Slot />`
  // content swaps), so without this it keeps the previous page's scroll
  // offset. Reset it on every route change so new pages start at the top.
  // Scrolling back to 0 re-fires `handleScroll`, which resets the header's
  // scroll-driven state too.
  useEffect(() => {
    scrollElementRef.current?.scrollTo({ top: 0 });
    window.scrollTo({ top: 0 });
  }, [pathname]);

  const navBarItems = useMemo(
    () =>
      routes
        // Filter out the predefined tabs that are also defined in the menu (to avoid duplicates)
        .filter((route) => !menu?.navBar.some((item) => item.name === route))
        // Make sure the ones left are present in the tab layout, but hidden from the tab bar
        .map<{
          name: string;
          label?: string;
          icon?: string;
          isHidden?: boolean;
        }>((route) => ({
          name: route,
          isHidden: true,
        }))
        // Add the valid menu items, so they are present in the nav bar
        .concat(menu?.navBar ?? [])
        // Filter out the ones that are hidden, because the nav bar doesn't do that automatically
        .filter((item) => !item.isHidden)
        // Apply a limit
        .slice(0, MAX_NAV_BAR_ITEMS),
    [menu, routes],
  );

  const tabBarItems = useMemo(
    () => (menu?.tabBar ?? []).slice(0, MAX_TAB_BAR_ITEMS),
    [menu],
  );

  return (
    <>
      <div {...getWebProps($.page)} onScroll={handleScroll}>
        <Hide mq={mq.only.width(undefined, "lg")}>
          <Animated.View style={[$.header, headerStyle]}>
            <View style={$.headerInner}>
              <Link href={homeHref} asChild>
                <Image
                  source={logo}
                  style={$.logo}
                  contentFit="contain"
                  contentPosition="left center"
                  accessibilityRole="image"
                  accessibilityLabel={t("COMMON/LOGO/LABEL")}
                />
              </Link>

              <View style={$.center}>
                <NavBar
                  items={navBarItems
                    // We don't want to show profile as item here, as it has a separate button
                    .filter((item) => item.name !== "profile")}
                />
              </View>

              <View style={$.right}>
                <Pressable
                  accessibilityRole="button"
                  accessibilityLabel={t("PROFILE/TAB_LABEL")}
                  style={[
                    $.profile,
                    isPathnameActive(pathname, profilePath) && $.profileActive,
                  ]}
                  onPress={() => router.navigate(profileHref)}
                >
                  <Icon name="person-outline" style={$.profileIcon} />
                </Pressable>
              </View>
            </View>
          </Animated.View>
        </Hide>

        <View style={$.content}>
          <Slot />
        </View>

        <Display mq={mq.only.width("lg")}>
          <View style={$.footer}>
            <Footer />
          </View>
        </Display>
      </div>

      <Display mq={mq.only.width(undefined, "lg")}>
        <TabBar style={$.tabBar} items={tabBarItems} />
      </Display>
    </>
  );
};

export default TabsLayout;
